<template>
  <div class="lavadero-kanban">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="info"
      class="mb-0"
    />

    <!-- Filtros rápidos + Refrescar -->
    <v-row class="lavadero-toolbar px-2 d-flex justify-space-between align-center" dense>
      <v-col cols="12" sm="6" md="4" class="lavadero-toolbar__field py-1 mt-2">
        <v-text-field
          v-model="quickSearch"
          class="lavadero-toolbar__input"
          label="Búsqueda rápida"
          hide-details
          outlined
          dense
          clearable
          placeholder="Patente, VIN..."
        />
      </v-col>
      <v-col cols="6" sm="3" md="2" class="lavadero-toolbar__field py-1 mt-2">
        <v-autocomplete
          v-model="filtroFecha"
          class="lavadero-toolbar__input"
          label="Fecha"
          :items="fechasOptions"
          item-text="text"
          item-value="value"
          hide-details
          outlined
          dense
        />
      </v-col>
      <v-col cols="6" sm="3" md="3" class="lavadero-toolbar__field py-1 mt-2">
        <v-autocomplete
          v-model="filtroCanal"
          class="lavadero-toolbar__input"
          label="Canal"
          :items="canalesOptions"
          item-text="text"
          item-value="value"
          placeholder="Todos"
          hide-details
          outlined
          dense
          clearable
        />
      </v-col>
       <v-col cols="12" md="3" class="lavadero-toolbar__actions py-1 mt-2">
        <v-btn
          class="lavadero-refresh-btn mr-2"
          color="primary"
          small
          :loading="loading"
          @click="fetchTurnos"
        >
          <v-icon left small>fas fa-sync-alt</v-icon>
          Refrescar
        </v-btn>
        <v-btn
          v-if="tienePermiso('taller.lavadero.metricas')"
          color="teal darken-1"
          small
          :to="'/taller/lavadero/metricas-historicas'"
        >
          <v-icon left small>fas fa-chart-bar</v-icon>
          Métricas
        </v-btn>
      </v-col>
    </v-row>

    <v-tabs
      v-model="mobileColumnTab"
      class="lavadero-mobile-tabs"
      background-color="transparent"
      grow
      show-arrows
      slider-color="primary"
    >
      <v-tab
        v-for="(columna, index) in columnasFiltradas"
        :key="`mobile-tab-${columna.estado}`"
        class="lavadero-mobile-tabs__tab"
        @click="scrollToMobileColumn(columna.estado, index)"
      >
        <span>{{ columna.titulo }}</span>
        <v-chip x-small label class="lavadero-mobile-tabs__count">
          {{ columna.turnos.length }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <!-- Tablero -->
    <v-row class="lavadero-board-row px-2 mt-2">
      <v-col cols="12" class="lavadero-board-col">
        <TableroLavadero
          ref="tableroLavadero"
          class="lavadero-board"
          :class="{ 'lavadero-board--mobile-single': isMobileKanban }"
          :columnas="columnasVisiblesKanban"
          :permisos="permisos"
          :current-username="currentUsername"
          :lavadores="lavadores"
          @drag-change="handleDragChange"
          @reassign="handleReassign"
          @cambiar-horario="handleCambiarHorario"
          @modificar-unidad="handleModificarUnidad"
          @swipe-action="handleSwipeAction"
        />
      </v-col>
    </v-row>

    <!-- FAB para crear turno manual -->
    <v-btn
      v-if="permisos.gestion || permisos.turnoManual || permisos.esLavador"
      fab
      dark
      color="primary"
      fixed
      bottom
      right
      class="lavadero-fab"
      title="Crear turno manual"
      @click="abrirDialogoCrearTurno"
    >
      <v-icon>fas fa-plus</v-icon>
    </v-btn>

    <!-- Diálogo: Crear Turno Manual -->
    <v-dialog
      v-model="dialogoCrearTurno"
      max-width="550px"
      persistent
    >
      <v-card class="lavadero-dialog lavadero-dialog--create">
        <v-card-title class="lavadero-dialog__title headline primary white--text">
          <v-icon left color="white" class="lavadero-dialog__title-icon">fas fa-plus-circle</v-icon>
          <span>Crear Turno Manual</span>
        </v-card-title>
        <v-card-text class="lavadero-dialog__body pt-4">
          <v-row dense class="mb-1">
            <!-- Búsqueda SAI: Patente, VIN o Chasis -->
            <v-col cols="12">
              <v-text-field
                v-model="nuevoTurno.patente"
                label="Buscar por Patente, VIN o Chasis *"
                outlined
                dense
                hide-details="auto"
                maxlength="17"
                :loading="buscandoVehiculo"
              />
              <div
                v-if="modeloEncontrado"
                class="text-body-2 grey--text text--darken-2 mt-1"
              >
                <v-icon x-small color="grey darken-2" class="mr-1">fas fa-car</v-icon>
                {{ modeloEncontrado }}
              </div>
              <div
                v-if="patenteDuplicada"
                class="text-caption orange--text text--darken-3 mt-1"
              >
                <v-icon x-small color="orange darken-3" class="mr-1">fas fa-exclamation-triangle</v-icon>
                Esta patente ya tiene un turno cargado. ¿Seguro que querés crear otro?
              </div>
            </v-col>
          </v-row>

          <v-row dense class="mb-1">
            <!-- Fecha y Hora -->
            <v-col cols="12" sm="6">
              <FechaPicker
                v-model="nuevoTurno.fecha"
                label="Fecha *"
                :filled="false"
                :hide-details="true"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu
                ref="menuHora"
                v-model="menuHora"
                :close-on-content-click="false"
                :return-value.sync="nuevoTurno.hora"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="nuevoTurno.hora"
                    label="Hora"
                    readonly
                    outlined
                    dense
                    hide-details="auto"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <template v-slot:append-outer>
                      <v-icon v-on="on" color="info" title="Seleccionar hora">
                        fas fa-clock
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-time-picker
                  v-if="menuHora"
                  v-model="nuevoTurno.hora"
                  full-width
                  format="24hr"
                  min="06:00"
                  max="20:00"
                  :allowed-hours="allowedHours"
                  @click:minute="$refs.menuHora.save(nuevoTurno.hora)"
                />
              </v-menu>
            </v-col>
          </v-row>

          <v-row dense class="mb-1">
            <v-col cols="12">
              <v-autocomplete
                v-model="nuevoTurno.area_id"
                label="Área"
                :items="areas"
                item-text="Nombre"
                item-value="Area_id"
                outlined
                dense
                hide-details="auto"
                clearable
                :loading="cargandoAreas"
                no-data-text="No se encontraron áreas"
              />
            </v-col>
          </v-row>

          <v-row dense class="mb-1">
            <v-col cols="12">
              <v-textarea
                v-model="nuevoTurno.comentario"
                label="Comentario (opcional)"
                outlined
                dense
                hide-details="auto"
                rows="2"
                auto-grow
                no-resize
              />
            </v-col>
          </v-row>

          <!-- Sucursal: hardcodeado a "1" por ahora — descomentar cuando haya multi-sucursal -->
          <!--
          <v-divider class="my-2" />
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="nuevoTurno.sucursal"
                label="Sucursal"
                :items="sucursalesFiltradas"
                item-text="provincia"
                item-value="provincia"
                outlined
                dense
                hide-details="auto"
                clearable
                no-data-text="No se encontraron sucursales"
              />
            </v-col>
          </v-row>
          -->
        </v-card-text>
        <v-divider />
        <v-card-actions class="lavadero-dialog__actions">
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--secondary"
            text
            color="grey darken-1"
            :disabled="creandoTurno"
            @click="cerrarDialogoCrearTurno"
          >
            Cancelar
          </v-btn>
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--primary"
            color="primary"
            :loading="creandoTurno"
            :disabled="!formularioCrearValido"
            @click="crearTurno"
          >
            <v-icon left small>fas fa-save</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Confirmar Finalización -->
    <v-dialog
      v-model="dialogoConfirmarFinalizar"
      max-width="400px"
    >
      <v-card class="lavadero-dialog lavadero-dialog--confirm">
        <v-card-title class="lavadero-dialog__title headline green white--text">
          <v-icon left color="white" class="lavadero-dialog__title-icon">fas fa-check-circle</v-icon>
          <span>Finalizar turno</span>
        </v-card-title>
        <v-card-text class="lavadero-dialog__body pt-4">
          <p class="text-body-1">
            ¿Estás seguro de que querés finalizar el turno
            <strong>{{ turnoAFinalizar?.patente || "—" }}</strong>?
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="lavadero-dialog__actions">
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--secondary"
            text
            color="grey darken-1"
            :disabled="finalizando"
            @click="cancelarFinalizacion"
          >
            Cancelar
          </v-btn>
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--primary"
            color="green"
            :loading="finalizando"
            @click="confirmarFinalizacion"
          >
            <v-icon left small>fas fa-check</v-icon>
            Finalizar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Confirmar vuelta a pendiente -->
    <v-dialog
      v-model="dialogoConfirmarPendiente"
      max-width="420px"
    >
      <v-card class="lavadero-dialog lavadero-dialog--confirm">
        <v-card-title class="lavadero-dialog__title headline amber darken-2 white--text">
          <v-icon left color="white" class="lavadero-dialog__title-icon">fas fa-undo</v-icon>
          <span>Volver a pendiente</span>
        </v-card-title>
        <v-card-text class="lavadero-dialog__body pt-4">
          <p class="text-body-1 mb-0">
            ¿Querés volver a pendiente el turno
            <strong>{{ turnoAPendiente?.patente || "—" }}</strong>?
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="lavadero-dialog__actions">
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--secondary"
            text
            color="grey darken-1"
            :disabled="volviendoAPendiente"
            @click="cancelarVueltaAPendiente"
          >
            Cancelar
          </v-btn>
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--primary"
            color="amber darken-2"
            :loading="volviendoAPendiente"
            @click="confirmarVueltaAPendiente"
          >
            <v-icon left small>fas fa-undo</v-icon>
            Volver a pendiente
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Confirmar Autoasignación -->
    <v-dialog
      v-model="dialogoConfirmarAutoasignacion"
      max-width="400px"
    >
      <v-card class="lavadero-dialog lavadero-dialog--confirm">
        <v-card-title class="lavadero-dialog__title headline blue white--text">
          <v-icon left color="white" class="lavadero-dialog__title-icon">fas fa-user-check</v-icon>
          <span>Asignar turno</span>
        </v-card-title>
        <v-card-text class="lavadero-dialog__body pt-4">
          <p class="text-body-1">
            ¿Querés asignarte el turno
            <strong>{{ turnoAAutoasignar?.patente || "—" }}</strong>?
          </p>
          <v-checkbox
            v-model="equipoSeleccionado"
            label="Equipo"
            hide-details
            class="mt-2"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="lavadero-dialog__actions">
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--secondary"
            text
            color="grey darken-1"
            :disabled="autoasignandoConfirmado"
            @click="cancelarAutoasignacion"
          >
            Cancelar
          </v-btn>
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--primary"
            color="blue"
            :loading="autoasignandoConfirmado"
            @click="confirmarAutoasignacion"
          >
            <v-icon left small>fas fa-check</v-icon>
            Asignar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Asignar Lavador -->
    <v-dialog
      v-model="dialogoAsignar"
      max-width="450px"
    >
      <v-card class="lavadero-dialog lavadero-dialog--assign">
        <v-card-title class="lavadero-dialog__title headline blue white--text">
          <v-icon left color="white" class="lavadero-dialog__title-icon">fas fa-user-check</v-icon>
          <span>{{ dialogoAsignarTitulo }}</span>
        </v-card-title>
        <v-card-text class="lavadero-dialog__body pt-4">
          <p class="text-body-2 mb-3">
            {{ dialogoAsignarSubtitulo }}
          </p>
          <v-autocomplete
            v-model="lavadorSeleccionado"
            label="Lavador"
            :items="lavadoresParaAsignar"
            item-value="id"
            :item-text="itemTextLavador"
            :loading="cargandoLavadores"
            outlined
            dense
            hide-details="auto"
            :disabled="esAutoasignacion && modoAsignacion !== 'reassign'"
            no-data-text="No se encontraron lavadores"
          >
            <template v-slot:selection="{ item }">
              {{ item.nombre }} ({{ item.username }})
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.nombre }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.username }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <v-checkbox
            v-model="equipoSeleccionado"
            label="Equipo"
            hide-details
            class="mt-2"
          />
          <div
            v-if="modoAsignacion === 'assign-finalize' && !lavadorSeleccionado"
            class="text-caption orange--text text--darken-3 mt-2"
          >
            <v-icon x-small color="orange darken-3" class="mr-1">fas fa-exclamation-triangle</v-icon>
            Sin lavador seleccionado — se registrará como "<strong>Cliente no asistió</strong>"
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="lavadero-dialog__actions">
          <template v-if="modoAsignacion === 'assign-finalize'">
            <div class="lavadero-dialog__action-row lavadero-dialog__action-row--top mb-2">
              <v-btn
                class="lavadero-dialog__btn"
                color="orange darken-1"
                :loading="asignando"
                @click="finalizarSinAsistenciaDesdeDialogo"
              >
                <v-icon left small>fas fa-user-slash</v-icon>
                No asistió
              </v-btn>
              <v-btn
                class="lavadero-dialog__btn"
                color="purple darken-1"
                :loading="asignando"
                @click="retirarAntesDesdeDialogo"
              >
                <v-icon left small>fas fa-sign-out-alt</v-icon>
                Retira antes
              </v-btn>
            </div>
            <div class="lavadero-dialog__action-row lavadero-dialog__action-row--bottom">
              <v-btn
                class="lavadero-dialog__btn lavadero-dialog__btn--secondary"
                text
                color="grey darken-1"
                :disabled="asignando"
                @click="cancelarAsignacion"
              >
                Cancelar
              </v-btn>
              <v-btn
                class="lavadero-dialog__btn lavadero-dialog__btn--primary"
                color="green"
                :loading="asignando"
                @click="confirmarAsignacion"
              >
                <v-icon left small>fas fa-check</v-icon>
                Asignar y Finalizar
              </v-btn>
            </div>
          </template>
          <template v-else>
            <v-btn
              class="lavadero-dialog__btn lavadero-dialog__btn--secondary"
              text
              color="grey darken-1"
              :disabled="asignando"
              @click="cancelarAsignacion"
            >
              Cancelar
            </v-btn>
            <v-btn
              class="lavadero-dialog__btn lavadero-dialog__btn--primary"
              color="blue"
              :loading="asignando"
              :disabled="!lavadorSeleccionado"
              @click="confirmarAsignacion"
            >
              <v-icon left small>fas fa-check</v-icon>
              Asignar
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Diálogo: Cambiar Horario de Retiro (Rada) -->
    <v-dialog
      v-model="dialogoCambiarHorario"
      max-width="480px"
    >
      <v-card class="lavadero-dialog lavadero-dialog--confirm">
        <v-card-title class="lavadero-dialog__title headline purple darken-1 white--text">
          <v-icon left color="white" class="lavadero-dialog__title-icon">fas fa-clock</v-icon>
          <span>Cambiar horario de retiro</span>
        </v-card-title>
        <v-card-text class="lavadero-dialog__body pt-4">
          <p class="text-body-2 mb-3">
            {{ turnoCambiarHorario ? (turnoCambiarHorario.patente || turnoCambiarHorario.vin || '—') + ' — ' + (turnoCambiarHorario.modelo || '—') : '' }}
          </p>
          <v-textarea
            v-model="comentarioHorario"
            label="Comentario"
            outlined
            dense
            hide-details="auto"
            rows="3"
            auto-grow
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="lavadero-dialog__actions">
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--secondary"
            text
            color="grey darken-1"
            :disabled="guardandoHorario"
            @click="cancelarCambioHorario"
          >
            Cancelar
          </v-btn>
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--primary"
            color="purple darken-1"
            :loading="guardandoHorario"
            @click="guardarCambioHorario"
          >
            <v-icon left small>fas fa-save</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Modificar unidad (Rada) -->
    <v-dialog
      v-model="dialogoModificarUnidad"
      max-width="400px"
      :persistent="guardandoUnidad"
    >
      <v-card class="lavadero-dialog lavadero-dialog--confirm">
        <v-card-title class="lavadero-dialog__title primary white--text">
          <v-icon left color="white" class="lavadero-dialog__title-icon">fas fa-car</v-icon>
          <span>Modificar unidad</span>
        </v-card-title>
        <v-card-text class="lavadero-dialog__body pt-4">
          <p class="text-body-2 mb-3">
            {{ turnoModificarUnidad ? (turnoModificarUnidad.modelo || "Unidad Rada") : "" }}
          </p>
          <v-text-field
            v-model="unidadModificar"
            label="Unidad *"
            outlined
            dense
            autofocus
            maxlength="20"
            counter="20"
            hide-details="auto"
            :disabled="guardandoUnidad"
            :error-messages="errorModificarUnidad ? [errorModificarUnidad] : []"
            @input="errorModificarUnidad = ''"
            @keyup.enter="guardarUnidad"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="lavadero-dialog__actions">
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--secondary"
            text
            color="grey darken-1"
            :disabled="guardandoUnidad"
            @click="cancelarModificarUnidad"
          >
            Cancelar
          </v-btn>
          <v-btn
            class="lavadero-dialog__btn lavadero-dialog__btn--primary"
            color="primary"
            :loading="guardandoUnidad"
            :disabled="!formularioModificarUnidadValido"
            @click="guardarUnidad"
          >
            <v-icon left small>fas fa-save</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import TableroLavadero from "@/components/lavadero/TableroLavadero.vue";
import FechaPicker from "@/components/util/FechaPicker.vue";

export default {
  name: "LavaderoKanban",
  components: {
    TableroLavadero,
    FechaPicker,
  },
  data() {
    return {
      filtros: {
        fecha_desde: null,
        fecha_hasta: null,
        sucursal: "1",
        estado: null,
      },
      quickSearch: "",
      filtroFecha: "todos",
      filtroCanal: null,
      fechasOptions: [
        { value: "todos", text: "Todos" },
        { value: "hoy", text: "Hoy" },
        { value: "manana", text: "Mañana" },
        { value: "proximos_7", text: "Próximos 7 días" },
      ],
      canalesOptions: [
        { value: null, text: "Todos" },
        { value: "entrega", text: "Entregas" },
        { value: "clientes", text: "Clientes" },
        { value: "rada", text: "Rada" },
        { value: "voucher", text: "Voucher" },
        { value: "manual", text: "Manual" },
      ],
      mobileColumnTab: 0,
      isMobileKanban: false,
      _refreshInterval: null,
      isDragging: false,
      estadosOptions: [
        { value: "pendiente", text: "Pendientes" },
        { value: "asignado", text: "Asignados" },
        { value: "finalizado", text: "Finalizados" },
        { value: "todos", text: "Todos" },
      ],
      // --- Crear turno manual ---
      dialogoCrearTurno: false,
      creandoTurno: false,
      menuHora: false,
      buscandoVehiculo: false,
      patenteDuplicada: false,
      modeloEncontrado: "",
      patenteEncontrada: "",
      cargandoAreas: false,
      nuevoTurno: {
        patente: "",
        vin: "",
        modelo: "",
        fecha: "",
        hora: "",
        sucursal: "1",
        area_id: null,
        comentario: "",
      },
      // --- Asignar lavador ---
      dialogoAsignar: false,
      asignando: false,
      lavadorSeleccionado: null,
      equipoSeleccionado: false,
      turnoEnAsignacion: null,
      movimientoPendiente: null,
      // Contexto: 'assign' = asignación inicial (pendiente→asignado), 'reassign' = reasignación,
      //          'assign-finalize' = pendiente→finalizado con lavador
      modoAsignacion: "assign",
      // --- Confirmar finalización ---
      dialogoConfirmarFinalizar: false,
      finalizando: false,
      turnoAFinalizar: null,
      dialogoConfirmarPendiente: false,
      volviendoAPendiente: false,
      turnoAPendiente: null,
      // --- Confirmar autoasignación ---
      dialogoConfirmarAutoasignacion: false,
      turnoAAutoasignar: null,
      autoasignandoConfirmado: false,
      // --- Cambiar horario de retiro (Rada) ---
      dialogoCambiarHorario: false,
      turnoCambiarHorario: null,
      comentarioHorario: "",
      guardandoHorario: false,
      // --- Modificar unidad de Rada ---
      dialogoModificarUnidad: false,
      turnoModificarUnidad: null,
      unidadModificar: "",
      guardandoUnidad: false,
      errorModificarUnidad: "",
      // --- Mobile back-button: cierra dialogs al presionar "atrás" ---
      _popStateDialogHandler: null,
    };
  },
  computed: {
    ...mapState({
      empresas: (state) => state.empresas,
      loading: (state) => state.lavadero.loading,
    }),
    permisos() {
      // 1. Source of truth: backend-fetched permissions (stored in Vuex)
      const storePermisos = this.$store.state.lavadero.permisos;
      if (storePermisos) {
        return {
          lectura: storePermisos.lectura || storePermisos.turnoManual || false,
          gestion: storePermisos.gestion || false,
          esAdmin: storePermisos.esAdmin || false,
          esLavador: storePermisos.esLavador || false,
          turnoManual: storePermisos.turnoManual || false,
          radaCambiarHorario: this.tienePermiso("taller.lavadero.rada.cambiar.horario") || storePermisos.esAdmin,
        };
      }

      // 2. Fallback: token-embedded permissions (if ever populated by backend)
      const usuario = this.$store.state.usuario;
      if (usuario && Object.keys(usuario).length > 0 && usuario.permisos) {
        const turnoManual = this.tienePermiso("taller.lavadero.turno.manual");
        return {
          lectura: this.tienePermiso("lavadero.lectura") || turnoManual,
          gestion: this.tienePermiso("lavadero.gestion"),
          esAdmin: this.tienePermiso("taller.lavadero.administrador"),
          esLavador: this.tienePermiso("taller.lavadero.lavador"),
          turnoManual,
          radaCambiarHorario: this.tienePermiso("taller.lavadero.rada.cambiar.horario") || this.tienePermiso("taller.lavadero.administrador"),
        };
      }

      // 3. Safe default: NEVER grant admin/gestion when permissions are unknown.
      //    This prevents lavadores like DTINTE from seeing Asignar Lavador dialog.
      return { lectura: false, gestion: false, esAdmin: false, esLavador: false, turnoManual: false, radaCambiarHorario: false };
    },
    userId() {
      // Intentar obtener el ID del lavador actual desde la lista de lavadores
      const currentUsername = this.normalizarUsername(this.currentUsername);
      const lav = this.lavadores.find(
        (l) => this.normalizarUsername(l.username) === currentUsername
      );
      return lav ? lav.id : null;
    },
    currentUsername() {
      return this.$store.state.username || "";
    },
    usuarioNombre() {
      const usuario = this.$store.state.usuario;
      return usuario?.nombre || usuario?.usuario || "";
    },
    turnos() {
      return this.$store.state.lavadero.turnos || [];
    },
    lavadores() {
      return this.$store.state.lavadero.lavadores || [];
    },
    /**
     * Lavadores aptos para asignación: excluye usuarios que solo tienen
     * función de administrador, sin ser también lavadores.
     */
    lavadoresParaAsignar() {
      return this.lavadores.filter((l) => {
        const funciones = String(l.funciones || '')
          .toLowerCase()
          .replace(/:/g, '.');
        return funciones.includes('taller.lavadero.lavador');
      });
    },
    areas() {
      return this.$store.state.lavadero.areas || [];
    },
    areaUsuarioId() {
      // El área del usuario vive en el backend (GET /lavadero/permisos → area_id),
      // no en el JWT (usuario.area_id no existe). null hasta que lleguen los permisos.
      return this.$store.state.lavadero.permisos?.area_id ?? null;
    },
    cargandoLavadores() {
      return false;
    },
    esAutoasignacion() {
      // turnoManual-only users cannot auto-assign themselves
      if (this.permisos.turnoManual && !this.permisos.gestion && !this.permisos.esAdmin) return false;

      // Lavador (sin admin): se autoasigna al arrastrar a "Asignados".
      // Si el permiso no se detecta por formato del token, también alcanza con
      // que el usuario actual exista en la lista de lavadores.
      return (this.permisos.esLavador || !!this.userId) && !this.permisos.esAdmin;
    },
    dialogoAsignarTitulo() {
      if (this.modoAsignacion === "reassign") return "Reasignar Lavador";
      if (this.modoAsignacion === "assign-finalize") return "Asignar y Finalizar";
      return "Asignar Lavador";
    },
    dialogoAsignarSubtitulo() {
      if (!this.turnoEnAsignacion) return "";
      const vehiculo = [
        this.turnoEnAsignacion.patente,
        this.turnoEnAsignacion.modelo,
      ]
        .filter(Boolean)
        .join(" — ");
      const hora = this.turnoEnAsignacion.hora || "";
      let base = vehiculo;
      if (hora) base += ` (${hora})`;
      if (this.esAutoasignacion && this.modoAsignacion !== "reassign") {
        base += " — Se te asignará automáticamente.";
      }
      return base;
    },
    columnasFiltradas() {
      const search = this.quickSearch
        ? this.quickSearch.toLowerCase().trim()
        : "";

      let turnos = this.turnos.filter((t) => t.fuente !== "rada");

      if (search) {
        turnos = turnos.filter((t) => {
          return (
            (t.patente && t.patente.toLowerCase().includes(search)) ||
            (t.vin && t.vin.toLowerCase().includes(search)) ||
            (t.cliente_nombre &&
              t.cliente_nombre.toLowerCase().includes(search)) ||
            (t.modelo && t.modelo.toLowerCase().includes(search))
          );
        });
      }

      if (this.filtroFecha !== "todos") {
        const hoy = moment().format("YYYY-MM-DD");
        const manana = moment().add(1, "day").format("YYYY-MM-DD");
        const finProximos7 = moment().add(6, "days").format("YYYY-MM-DD");

        turnos = turnos.filter((t) => {
          const fecha = this.normalizarFechaTurno(t.fecha);
          if (!fecha) return false;
          if (this.filtroFecha === "hoy") return fecha === hoy;
          if (this.filtroFecha === "manana") return fecha === manana;
          return fecha >= hoy && fecha <= finProximos7;
        });
      }

      if (this.filtroCanal) {
        if (this.filtroCanal === 'clientes') {
          turnos = turnos.filter((t) => t.fuente === 'turno_cliente' || t.fuente === 'orden_servicio');
        } else {
          turnos = turnos.filter((t) => (t.canal || t.fuente) === this.filtroCanal);
        }
      }

      return [
        {
          titulo: "Pendientes",
          estado: "pendiente",
          turnos: turnos.filter((t) => t.estado_lavadero === "pendiente"),
        },
        {
          titulo: "Asignados",
          estado: "asignado",
          turnos: turnos.filter((t) => t.estado_lavadero === "asignado"),
        },
        {
          titulo: "Finalizados",
          estado: "finalizado",
          turnos: turnos.filter((t) => t.estado_lavadero === "finalizado"),
        },
      ];
    },
    columnasVisiblesKanban() {
      if (!this.isMobileKanban) return this.columnasFiltradas;

      const columnaActiva = this.columnasFiltradas[this.mobileColumnTab];
      return columnaActiva ? [columnaActiva] : this.columnasFiltradas.slice(0, 1);
    },
    formularioCrearValido() {
      return !!(
        this.nuevoTurno.patente &&
        this.nuevoTurno.patente.trim() &&
        this.nuevoTurno.fecha
      );
    },
    formularioModificarUnidadValido() {
      const unidad = String(this.unidadModificar || "").trim();
      return unidad.length > 0 && unidad.length <= 20;
    },
    sucursalesFiltradas() {
      const sucursales = this.$store.state.genericos.sucursales || [];
      return sucursales.filter((s) => s.empresa_id === 1);
    },
    anyDialogOpen() {
      return (
        this.dialogoCrearTurno ||
        this.dialogoConfirmarFinalizar ||
        this.dialogoConfirmarPendiente ||
        this.dialogoConfirmarAutoasignacion ||
        this.dialogoAsignar ||
        this.dialogoCambiarHorario ||
        this.dialogoModificarUnidad
      );
    },
  },
  watch: {
    /**
     * Cuando cambia la patente, busca en SAI para autocompletar VIN/Modelo.
     * Usa debounce de 400ms para no saturar el backend.
     */
    "nuevoTurno.patente": function (val) {
      this.onPatenteInput(val);
    },
    areas() {
      this.autocompletarAreaRadaUsuario();
    },
    areaUsuarioId() {
      // Si el diálogo ya está abierto cuando llegan los permisos, autocompleta igual.
      this.autocompletarAreaRadaUsuario();
    },
    /**
     * Sincroniza el historial del navegador para que el botón "atrás"
     * del celular cierre los dialogs en vez de navegar fuera de la página.
     */
    anyDialogOpen(val) {
      this._syncDialogHistory(val);
    },
    dialogoAsignar(val, oldVal) {
      if (!val && oldVal && !this.asignando && (this.turnoEnAsignacion || this.movimientoPendiente)) {
        this.cancelarAsignacion();
      }
    },
    dialogoConfirmarFinalizar(val, oldVal) {
      if (!val && oldVal && !this.finalizando && (this.turnoAFinalizar || this.movimientoPendiente)) {
        this.cancelarFinalizacion();
      }
    },
    dialogoConfirmarPendiente(val, oldVal) {
      if (!val && oldVal && !this.volviendoAPendiente && (this.turnoAPendiente || this.movimientoPendiente)) {
        this.cancelarVueltaAPendiente();
      }
    },
    dialogoConfirmarAutoasignacion(val, oldVal) {
      if (!val && oldVal && !this.autoasignandoConfirmado && (this.turnoAAutoasignar || this.movimientoPendiente)) {
        this.cancelarAutoasignacion();
      }
    },
    dialogoCambiarHorario(val, oldVal) {
      if (!val && oldVal && !this.guardandoHorario && this.turnoCambiarHorario) {
        this.cancelarCambioHorario();
      }
    },
    dialogoModificarUnidad(val, oldVal) {
      if (!val && oldVal && !this.guardandoUnidad && this.turnoModificarUnidad) {
        this.cancelarModificarUnidad();
      }
    },
    isMobileKanban() {
      this._syncDialogHistory(this.anyDialogOpen);
    },
  },
  methods: {
    normalizarCodigoPermiso(codigo) {
      return String(codigo || "")
        .trim()
        .toLowerCase()
        .replace(/:/g, ".");
    },
    normalizarUsername(username) {
      return String(username || "")
        .trim()
        .toUpperCase();
    },
    normalizarFechaTurno(fecha) {
      const valor = String(fecha || "").trim();
      const fechaISO = valor.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (fechaISO) return `${fechaISO[1]}-${fechaISO[2]}-${fechaISO[3]}`;

      const fechaLocal = valor.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (fechaLocal) return `${fechaLocal[3]}-${fechaLocal[2]}-${fechaLocal[1]}`;

      return "";
    },
    tienePermiso(codigo) {
      const codigoNormalizado = this.normalizarCodigoPermiso(codigo);
      const permisos = this.$store.state.usuario?.permisos;
      if (Array.isArray(permisos)) {
        return permisos.some((permiso) => {
          if (typeof permiso === "string") {
            return this.normalizarCodigoPermiso(permiso) === codigoNormalizado;
          }

          if (permiso && typeof permiso === "object") {
            const valor = permiso.codigo || permiso.Codigo || permiso.nombre || permiso.Nombre || permiso.funcion || permiso.Funcion;
            return this.normalizarCodigoPermiso(valor) === codigoNormalizado;
          }

          return false;
        });
      }

      if (typeof permisos === "string") {
        return permisos
          .split(",")
          .map((p) => p.trim())
          .some((p) => this.normalizarCodigoPermiso(p) === codigoNormalizado);
      }

      if (permisos && typeof permisos === "object") {
        return Object.keys(permisos).some((key) => {
          return this.normalizarCodigoPermiso(key) === codigoNormalizado && !!permisos[key];
        });
      }

      return false;
    },
    itemTextLavador(item) {
      return `${item.nombre} (${item.username})`;
    },
    esTurnoPropio(turno) {
      const turnoLavadorId = parseInt(turno?.lavador_id, 10);
      const currentLavadorId = parseInt(this.userId, 10);

      if (Number.isNaN(turnoLavadorId) || Number.isNaN(currentLavadorId)) {
        return false;
      }

      return turnoLavadorId === currentLavadorId;
    },
    async asegurarLavadoresCargados() {
      if (this.lavadores.length > 0) return true;

      this.$store.dispatch("show_snackbar", {
        text: "Cargando lista de lavadores... Intentá de nuevo.",
        color: "info",
      });
      await this.$store.dispatch("lavadero/fetchLavadores");

      if (this.lavadores.length === 0) {
        return false;
      }

      return true;
    },
    abrirConfirmacionAutoasignacion(turno) {
      this.turnoAAutoasignar = turno;
      this.equipoSeleccionado = !!turno?.equipo;
      this.dialogoConfirmarAutoasignacion = true;
    },
    mostrarErrorLavadorNoEncontrado() {
      this.$store.dispatch("show_snackbar", {
        text: "No se encontró tu usuario en la lista de lavadores. Contactá a un administrador.",
        color: "error",
      });
    },
    puedeMoverTurno(turno, fromEstado, toEstado) {
      // turnoManual-only users cannot move cards between columns
      if (this.permisos.turnoManual && !this.permisos.gestion && !this.permisos.esAdmin) {
        return false;
      }

      if (this.permisos.esAdmin) {
        return true;
      }

      if (this.esAutoasignacion) {
        if (fromEstado === "pendiente" && toEstado === "asignado") {
          return true;
        }

        if (fromEstado === "pendiente" && toEstado === "finalizado") {
          return true;
        }

        if (fromEstado === "asignado" && toEstado === "finalizado") {
          return this.esTurnoPropio(turno);
        }

        if (fromEstado === "asignado" && toEstado === "pendiente") {
          return this.esTurnoPropio(turno);
        }

        return false;
      }

      return this.permisos.gestion;
    },
    mostrarErrorPermisosMovimiento(fromEstado, toEstado) {
      let text = "No tenés permisos para mover turnos";

      if (this.permisos.esLavador && fromEstado === "asignado" && toEstado === "finalizado") {
        text = "Solo podés finalizar turnos asignados a tu usuario";
      } else if (this.permisos.esLavador && fromEstado === "asignado" && toEstado === "pendiente") {
        text = "Solo podés volver a pendiente turnos asignados a tu usuario y dentro de los primeros 5 minutos";
      } else if (this.permisos.esLavador && fromEstado === "pendiente" && toEstado === "finalizado") {
        text = "No tenés permisos para finalizar turnos pendientes";
      }

      this.$store.dispatch("show_snackbar", {
        text,
        color: "warning",
      });
    },
    async fetchTurnos() {
      await this.$store.dispatch("lavadero/fetchTurnos", this.filtros);
      // Disparar snapshot diario automático para Salta (fire-and-forget, silencioso)
      this.$store.dispatch("lavadero/guardarSnapshotDiario", {
        fecha_dia: moment().format("YYYY-MM-DD"),
        sucursal: 1,
        silent: true,
      }).catch(() => {}); // ignorar errores para no interrumpir el flujo
    },
    buscar() {
      this.fetchTurnos();
    },
    updateMobileKanbanLayout() {
      this.isMobileKanban =
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 599px)").matches;
    },
    scrollToMobileColumn(estado, index) {
      this.mobileColumnTab = index;
      if (this.isMobileKanban) return;

      this.$nextTick(() => {
        const tablero = this.$refs.tableroLavadero;
        if (tablero && typeof tablero.scrollToColumn === "function") {
          tablero.scrollToColumn(estado);
        }
      });
    },
    guardarMovimientoPendiente(turno, fromEstado, toEstado, newIndex, oldIndex) {
      this.movimientoPendiente = {
        turno: JSON.parse(JSON.stringify(turno)),
        fromEstado,
        toEstado,
        newIndex: newIndex || 0,
        oldIndex: oldIndex || 0,
      };
    },
    aplicarMovimientoPendiente(updates = {}, toEstado) {
      const tablero = this.$refs.tableroLavadero;
      if (!this.movimientoPendiente || !tablero || typeof tablero.moveTurnoLocal !== "function") return;

      tablero.moveTurnoLocal({
        turno: this.movimientoPendiente.turno,
        toEstado: toEstado || this.movimientoPendiente.toEstado,
        updates,
        index: this.movimientoPendiente.newIndex,
      });
    },
    actualizarTurnoLocal(turno, updates = {}) {
      const tablero = this.$refs.tableroLavadero;
      if (!tablero || typeof tablero.updateTurnoLocal !== "function") return;

      tablero.updateTurnoLocal(turno, updates);
    },
    revertirMovimientoPendiente() {
      const tablero = this.$refs.tableroLavadero;
      if (!this.movimientoPendiente || !tablero || typeof tablero.revertTurnoLocal !== "function") return;

      tablero.revertTurnoLocal(this.movimientoPendiente);
    },
    limpiarMovimientoPendiente() {
      this.movimientoPendiente = null;
    },
    limpiarFiltros() {
      this.filtros = {
        fecha_desde: null,
        fecha_hasta: null,
        sucursal: null,
        estado: null,
      };
      this.quickSearch = "";
      this.filtroFecha = "todos";
      this.filtroCanal = null;
      this.fetchTurnos();
    },

    /**
     * Maneja el evento drag-change del TableroLavadero.
     * Si es pendiente→asignado: abre el diálogo de asignación.
     * Otros movimientos: persiste directamente.
     */
    async handleDragChange({ turno, fromEstado, toEstado, newIndex, oldIndex }) {
      this.guardarMovimientoPendiente(turno, fromEstado, toEstado, newIndex, oldIndex);

      // Pendiente → Asignado:
      // - lavador: confirmación + autoasignación
      // - administrador: selector de lavadores
      // - resto: sin permiso
      if (fromEstado === "pendiente" && toEstado === "asignado") {
        // turnoManual-only users cannot move cards — block early before any dialog
        if (this.permisos.turnoManual && !this.permisos.gestion && !this.permisos.esAdmin) {
          this.mostrarErrorPermisosMovimiento(fromEstado, toEstado);
          this.revertirMovimientoPendiente();
          this.limpiarMovimientoPendiente();
          return;
        }

        const lavadoresCargados = await this.asegurarLavadoresCargados();
        if (!lavadoresCargados) {
          this.revertirMovimientoPendiente();
          this.limpiarMovimientoPendiente();
          return;
        }

        if (this.permisos.esAdmin) {
          this.turnoEnAsignacion = turno;
          this.modoAsignacion = "assign";
          this.lavadorSeleccionado = null;
          this.equipoSeleccionado = !!turno.equipo;
          this.dialogoAsignar = true;
          return;
        }

        if ((this.permisos.esLavador || this.esAutoasignacion) && this.userId) {
          this.abrirConfirmacionAutoasignacion(turno);
          return;
        }

        if ((this.permisos.esLavador || this.esAutoasignacion) && !this.userId) {
          this.revertirMovimientoPendiente();
          this.limpiarMovimientoPendiente();
          this.mostrarErrorLavadorNoEncontrado();
          return;
        }

        if (!this.permisos.esAdmin) {
          this.mostrarErrorPermisosMovimiento(fromEstado, toEstado);
          this.revertirMovimientoPendiente();
          this.limpiarMovimientoPendiente();
          return;
        }

        return;
      }

      if (!this.puedeMoverTurno(turno, fromEstado, toEstado)) {
        this.mostrarErrorPermisosMovimiento(fromEstado, toEstado);
        this.revertirMovimientoPendiente();
        this.limpiarMovimientoPendiente();
        return;
      }

      // Pendiente → Finalizado: abrir diálogo de asignación + finalización
      if (fromEstado === "pendiente" && toEstado === "finalizado") {
        const lavadoresCargados = await this.asegurarLavadoresCargados();
        if (!lavadoresCargados) {
          this.revertirMovimientoPendiente();
          this.limpiarMovimientoPendiente();
          return;
        }

        this.turnoEnAsignacion = turno;
        this.modoAsignacion = "assign-finalize";
        // Pre-seleccionar al lavador actual para que pueda "Asignar y Finalizar"
        // Si no es lavador (admin/gestion), arranca sin selección para que elija
        this.lavadorSeleccionado = this.esAutoasignacion && this.userId ? this.userId : null;
        this.equipoSeleccionado = !!turno.equipo;
        this.dialogoAsignar = true;
        return;
      }

      // Asignado → Finalizado: abrir diálogo de confirmación
      if (fromEstado === "asignado" && toEstado === "finalizado") {
        this.turnoAFinalizar = turno;
        this.dialogoConfirmarFinalizar = true;
        return;
      }

      if (fromEstado === "asignado" && toEstado === "pendiente") {
        this.turnoAPendiente = turno;
        this.dialogoConfirmarPendiente = true;
        return;
      }

      // Otros movimientos: persistir directamente
      this.isDragging = true;
      try {
        const result = await this.$store.dispatch("lavadero/cambiarEstado", {
          id: turno.id,
          fuente: turno.fuente,
          nuevo_estado: toEstado,
        });
        if (!result || result.exito !== 1) {
          this.revertirMovimientoPendiente();
          this.limpiarMovimientoPendiente();
          return;
        }
        const updates = { estado_lavadero: toEstado };
        if (toEstado === "pendiente") {
          updates.lavador_id = null;
          updates.lavador_nombre = "";
          updates.equipo = 0;
        }
        this.aplicarMovimientoPendiente(updates, toEstado);
        this.limpiarMovimientoPendiente();
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text:
            "Error al mover el turno: " + (err.message || "Error desconocido"),
          color: "error",
        });
        this.revertirMovimientoPendiente();
        this.limpiarMovimientoPendiente();
      } finally {
        this.isDragging = false;
      }
    },

    /**
     * Acciones por gesto mobile sobre una tarjeta.
     * - Pendiente + derecha: asignar.
     * - Pendiente + izquierda: finalizar.
     * - Asignado + derecha: confirmar finalización.
     */
    async handleSwipeAction({ turno, direction }) {
      if (!turno || !direction) return;

      const estado = turno.estado_lavadero;

      if (estado === "pendiente" && direction === "right") {
        const lavadoresCargados = await this.asegurarLavadoresCargados();
        if (!lavadoresCargados) return;

        await this.handleDragChange({
          turno,
          fromEstado: "pendiente",
          toEstado: "asignado",
        });
        return;
      }

      if (estado === "pendiente" && direction === "left") {
        if (!this.puedeMoverTurno(turno, "pendiente", "finalizado")) {
          this.mostrarErrorPermisosMovimiento("pendiente", "finalizado");
          return;
        }

        await this.handleDragChange({
          turno,
          fromEstado: "pendiente",
          toEstado: "finalizado",
        });
        return;
      }

      if (estado === "asignado" && direction === "right") {
        if (!this.puedeMoverTurno(turno, "asignado", "finalizado")) {
          this.mostrarErrorPermisosMovimiento("asignado", "finalizado");
          return;
        }

        await this.handleDragChange({
          turno,
          fromEstado: "asignado",
          toEstado: "finalizado",
        });
        return;
      }

      if (estado === "asignado" && direction === "left") {
        if (!this.puedeMoverTurno(turno, "asignado", "pendiente")) {
          this.mostrarErrorPermisosMovimiento("asignado", "pendiente");
          return;
        }

        await this.handleDragChange({
          turno,
          fromEstado: "asignado",
          toEstado: "pendiente",
        });
      }
    },

    /**
     * Autoasignación rápida para lavador común desde gesto mobile.
     * Evita abrir el diálogo cuando el turno pendiente se asigna al usuario actual.
     */
    async autoasignarTurnoDesdeGesto(turno) {
      const equipo = this.equipoSeleccionado ? 1 : 0;
      const lavador = this.lavadores.find(
        (l) => parseInt(l.id, 10) === parseInt(this.userId, 10)
      );

      if (!lavador) {
        this.revertirMovimientoPendiente();
        this.limpiarMovimientoPendiente();
        this.mostrarErrorLavadorNoEncontrado();
        return false;
      }

      this.asignando = true;

      try {
        const result = await this.$store.dispatch("lavadero/asignarLavador", {
          id: turno.id,
          lavador_id: this.userId,
          lavador_nombre: lavador.nombre,
          equipo,
        });

        if (!result || result.exito !== 1) {
          this.revertirMovimientoPendiente();
          this.limpiarMovimientoPendiente();
          return false;
        }

        await this.$store.dispatch("lavadero/cambiarEstado", {
          id: turno.id,
          fuente: turno.fuente,
          nuevo_estado: "asignado",
        });
        this.aplicarMovimientoPendiente({
          estado_lavadero: "asignado",
          lavador_id: this.userId,
          lavador_nombre: lavador.nombre,
          equipo,
        }, "asignado");
        this.limpiarMovimientoPendiente();

        this.$store.dispatch("show_snackbar", {
          text: "Turno asignado a tu usuario",
          color: "success",
        });
        return true;
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al asignar: " + (err.message || "Error desconocido"),
          color: "error",
        });
        this.revertirMovimientoPendiente();
        this.limpiarMovimientoPendiente();
        return false;
      } finally {
        this.asignando = false;
      }
    },

    /**
     * Finaliza un turno pendiente como "Cliente no asistió" sin asignar lavador.
     * Usado por lavadores comunes cuando mueven un turno directo a Finalizados.
     */
    async finalizarSinAsistencia(turno) {
      this.asignando = true;
      try {
        await this.$store.dispatch("lavadero/cambiarEstado", {
          id: turno.id,
          fuente: turno.fuente,
          nuevo_estado: "finalizado",
          incidentes: "Cliente no asistió",
        });
        this.$store.dispatch("show_snackbar", {
          text: "Turno finalizado — Cliente no asistió",
          color: "success",
        });
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al finalizar: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        this.asignando = false;
        this.fetchTurnos();
      }
    },

    cancelarAutoasignacion() {
      this.dialogoConfirmarAutoasignacion = false;
      this.turnoAAutoasignar = null;
      this.equipoSeleccionado = false;
      this.revertirMovimientoPendiente();
      this.limpiarMovimientoPendiente();
    },

    async confirmarAutoasignacion() {
      if (!this.turnoAAutoasignar) return;
      this.autoasignandoConfirmado = true;
      try {
        await this.autoasignarTurnoDesdeGesto(this.turnoAAutoasignar);
      } finally {
        this.autoasignandoConfirmado = false;
        this.dialogoConfirmarAutoasignacion = false;
        this.turnoAAutoasignar = null;
        this.equipoSeleccionado = false;
      }
    },

    /**
     * Confirma la asignación desde el diálogo.
     * Primero asigna el lavador, luego cambia el estado a 'asignado'.
     */
    async confirmarAsignacion() {
      if (!this.turnoEnAsignacion) return;
      if (this.modoAsignacion !== "assign-finalize" && !this.lavadorSeleccionado) return;

      // Defensive guard: non-admin lavador must only assign to themselves
      if (
        !this.permisos.esAdmin &&
        this.lavadorSeleccionado &&
        parseInt(this.lavadorSeleccionado, 10) !== parseInt(this.userId, 10)
      ) {
        this.$store.dispatch("show_snackbar", {
          text: "Solo podés asignarte turnos a vos mismo.",
          color: "warning",
        });
        this.dialogoAsignar = false;
        this.revertirMovimientoPendiente();
        this.limpiarMovimientoPendiente();
        return;
      }

      this.asignando = true;

      try {
        const lavador = this.lavadores.find(
          (l) => parseInt(l.id, 10) === parseInt(this.lavadorSeleccionado, 10)
        );
        const lavadorNombre = lavador ? lavador.nombre : "";

        // Modo assign-finalize: asignar lavador + finalizar en una sola llamada
        if (this.modoAsignacion === "assign-finalize") {
          try {
            const payload = {
              id: this.turnoEnAsignacion.id,
              fuente: this.turnoEnAsignacion.fuente,
              nuevo_estado: "finalizado",
            };
            if (this.lavadorSeleccionado) {
              payload.lavador_id = parseInt(this.lavadorSeleccionado, 10);
              payload.lavador_nombre = lavadorNombre;
              payload.equipo = this.equipoSeleccionado ? 1 : 0;
            } else {
              payload.incidentes = "Cliente no asistió";
            }
            await this.$store.dispatch("lavadero/cambiarEstado", payload);
            this.aplicarMovimientoPendiente({
              estado_lavadero: "finalizado",
              lavador_id: payload.lavador_id || null,
              lavador_nombre: payload.lavador_nombre || "",
              equipo: payload.equipo || 0,
            }, "finalizado");
            this.limpiarMovimientoPendiente();
            this.$store.dispatch("show_snackbar", {
              text: this.lavadorSeleccionado
                ? "Turno finalizado correctamente"
                : "Turno finalizado — Cliente no asistió",
              color: "success",
            });
          } catch (err) {
            this.$store.dispatch("show_snackbar", {
              text: "Error al finalizar: " + (err.message || "Error desconocido"),
              color: "error",
            });
            this.revertirMovimientoPendiente();
            this.limpiarMovimientoPendiente();
          } finally {
            this.dialogoAsignar = false;
          }
          return;
        }

        // 1. Asignar lavador
        const result = await this.$store.dispatch("lavadero/asignarLavador", {
          id: this.turnoEnAsignacion.id,
          lavador_id: this.lavadorSeleccionado,
          lavador_nombre: lavadorNombre,
          equipo: this.equipoSeleccionado ? 1 : 0,
        });

        if (result.exito !== 1) {
          this.dialogoAsignar = false;
          this.revertirMovimientoPendiente();
          this.limpiarMovimientoPendiente();
          return;
        }

        // 2. Si es asignación inicial (assign), cambiar estado a 'asignado'
        if (this.modoAsignacion === "assign") {
          try {
            await this.$store.dispatch("lavadero/cambiarEstado", {
              id: this.turnoEnAsignacion.id,
              fuente: this.turnoEnAsignacion.fuente,
              nuevo_estado: "asignado",
            });
            this.aplicarMovimientoPendiente({
              estado_lavadero: "asignado",
              lavador_id: this.lavadorSeleccionado,
              lavador_nombre: lavadorNombre,
              equipo: this.equipoSeleccionado ? 1 : 0,
            }, "asignado");
            this.limpiarMovimientoPendiente();
          } catch (err) {
            this.revertirMovimientoPendiente();
            this.limpiarMovimientoPendiente();
          }
        } else {
          this.actualizarTurnoLocal(this.turnoEnAsignacion, {
            lavador_id: this.lavadorSeleccionado,
            lavador_nombre: lavadorNombre,
            equipo: this.equipoSeleccionado ? 1 : 0,
          });
        }
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al asignar: " + (err.message || "Error desconocido"),
          color: "error",
        });
        this.revertirMovimientoPendiente();
        this.limpiarMovimientoPendiente();
      } finally {
        this.asignando = false;
        this.dialogoAsignar = false;
      }
    },

    cancelarAsignacion() {
      this.dialogoAsignar = false;
      this.turnoEnAsignacion = null;
      this.lavadorSeleccionado = null;
      this.equipoSeleccionado = false;
      this.revertirMovimientoPendiente();
      this.limpiarMovimientoPendiente();
    },

    /**
     * Finaliza un turno como "Cliente no asistió" directamente desde el diálogo
     * de asignación (modo assign-finalize), sin asignar lavador.
     */
    async finalizarSinAsistenciaDesdeDialogo() {
      if (!this.turnoEnAsignacion) return;

      this.asignando = true;
      try {
        await this.$store.dispatch("lavadero/cambiarEstado", {
          id: this.turnoEnAsignacion.id,
          fuente: this.turnoEnAsignacion.fuente,
          nuevo_estado: "finalizado",
          incidentes: "Cliente no asistió",
        });
        this.aplicarMovimientoPendiente({
          estado_lavadero: "finalizado",
          lavador_id: null,
          lavador_nombre: "",
          equipo: 0,
        }, "finalizado");
        this.limpiarMovimientoPendiente();
        this.$store.dispatch("show_snackbar", {
          text: "Turno finalizado — Cliente no asistió",
          color: "success",
        });
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al finalizar: " + (err.message || "Error desconocido"),
          color: "error",
        });
        this.revertirMovimientoPendiente();
        this.limpiarMovimientoPendiente();
      } finally {
        this.asignando = false;
        this.dialogoAsignar = false;
      }
    },

    /**
     * Finaliza un turno como "Cliente retiró antes" directamente desde el diálogo
     * de asignación (modo assign-finalize), sin asignar lavador.
     */
    async retirarAntesDesdeDialogo() {
      if (!this.turnoEnAsignacion) return;

      this.asignando = true;
      try {
        await this.$store.dispatch("lavadero/cambiarEstado", {
          id: this.turnoEnAsignacion.id,
          fuente: this.turnoEnAsignacion.fuente,
          nuevo_estado: "finalizado",
          incidentes: "Cliente retiró antes",
        });
        this.aplicarMovimientoPendiente({
          estado_lavadero: "finalizado",
          lavador_id: null,
          lavador_nombre: "",
          equipo: 0,
        }, "finalizado");
        this.limpiarMovimientoPendiente();
        this.$store.dispatch("show_snackbar", {
          text: "Turno finalizado — Cliente retiró antes",
          color: "success",
        });
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al finalizar: " + (err.message || "Error desconocido"),
          color: "error",
        });
        this.revertirMovimientoPendiente();
        this.limpiarMovimientoPendiente();
      } finally {
        this.asignando = false;
        this.dialogoAsignar = false;
      }
    },

    /**
     * Maneja la reasignación desde el menú de 3 puntos.
     */
    handleReassign(turno) {
      if (!this.permisos.esAdmin) {
        this.$store.dispatch("show_snackbar", {
          text: "No tenés permisos para reasignar turnos",
          color: "warning",
        });
        return;
      }

      this.turnoEnAsignacion = turno;
      this.modoAsignacion = "reassign";
      this.lavadorSeleccionado = turno.lavador_id || null;
      this.equipoSeleccionado = !!turno.equipo;
      this.dialogoAsignar = true;
    },

    /**
     * Abre el diálogo para cambiar el horario de retiro de un turno Rada.
     */
    handleCambiarHorario(turno) {
      if (!this.permisos.radaCambiarHorario && !this.permisos.esAdmin) {
        this.$store.dispatch("show_snackbar", {
          text: "No tenés permisos para cambiar el horario de retiro",
          color: "warning",
        });
        return;
      }

      // Pre-llenar con el comentario existente o derivarlo de pickup_time
      const comentarioExistente = turno.comentario || "";
      if (comentarioExistente) {
        this.comentarioHorario = comentarioExistente;
      } else if (turno.pickup_time) {
        this.comentarioHorario = `El cliente retira a las ${turno.pickup_time}`;
      } else {
        this.comentarioHorario = "";
      }

      this.turnoCambiarHorario = turno;
      this.dialogoCambiarHorario = true;
    },

    puedeModificarUnidadTurno(turno) {
      return !!(
        turno &&
        turno.fuente === "rada" &&
        turno.estado_lavadero !== "finalizado" &&
        (this.permisos.esLavador || this.permisos.esAdmin)
      );
    },

    handleModificarUnidad(turno) {
      if (!this.puedeModificarUnidadTurno(turno)) {
        this.$store.dispatch("show_snackbar", {
          text: "No tenés permisos para modificar la unidad",
          color: "warning",
        });
        return;
      }

      this.turnoModificarUnidad = turno;
      const patente = String(turno.patente || "").trim();
      this.unidadModificar = patente || String(turno.vin || "").trim();
      this.errorModificarUnidad = "";
      this.dialogoModificarUnidad = true;
    },

    async guardarUnidad() {
      const turno = this.turnoModificarUnidad;
      if (!turno) return;

      const unidad = String(this.unidadModificar || "").trim();
      if (!unidad) {
        this.errorModificarUnidad = "La unidad es obligatoria";
        return;
      }
      if (unidad.length > 20) {
        this.errorModificarUnidad = "La unidad no puede superar los 20 caracteres";
        return;
      }
      if (!this.puedeModificarUnidadTurno(turno)) {
        this.errorModificarUnidad = "No tenés permisos para modificar la unidad";
        return;
      }

      this.guardandoUnidad = true;
      this.errorModificarUnidad = "";
      try {
        const result = await this.$store.dispatch("lavadero/modificarUnidad", {
          id: turno.id,
          unidad,
        });

        if (!result || result.exito !== 1) {
          this.errorModificarUnidad =
            result?.message || "No se pudo actualizar la unidad";
          return;
        }

        const unidadActualizada = result.unidad || unidad.toUpperCase();
        this.actualizarTurnoLocal(turno, {
          patente: unidadActualizada,
        });
        this.dialogoModificarUnidad = false;
        this.turnoModificarUnidad = null;
        this.unidadModificar = "";
        this.errorModificarUnidad = "";
      } catch (err) {
        this.errorModificarUnidad =
          "Error al guardar: " + (err.message || "Error desconocido");
      } finally {
        this.guardandoUnidad = false;
      }
    },

    cancelarModificarUnidad() {
      this.dialogoModificarUnidad = false;
      this.turnoModificarUnidad = null;
      this.unidadModificar = "";
      this.errorModificarUnidad = "";
    },

    /**
     * Guarda el cambio de horario de retiro.
     */
    async guardarCambioHorario() {
      if (!this.turnoCambiarHorario) return;

      this.guardandoHorario = true;
      try {
        const result = await this.$store.dispatch("lavadero/actualizarComentario", {
          id: this.turnoCambiarHorario.id,
          comentario: this.comentarioHorario.trim() || null,
        });

        if (result.exito !== 1) {
          return;
        }

        // Actualizar localmente sin necesidad de refrescar todo
        this.actualizarTurnoLocal(this.turnoCambiarHorario, {
          comentario: this.comentarioHorario.trim() || null,
        });

        this.dialogoCambiarHorario = false;
        this.turnoCambiarHorario = null;
        this.comentarioHorario = "";
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al guardar: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        this.guardandoHorario = false;
      }
    },

    /**
     * Cancela el diálogo de cambio de horario.
     */
    cancelarCambioHorario() {
      this.dialogoCambiarHorario = false;
      this.turnoCambiarHorario = null;
      this.comentarioHorario = "";
    },

    /**
     * Confirma la finalización desde el diálogo de confirmación.
     */
    async confirmarFinalizacion() {
      if (!this.turnoAFinalizar) return;

      this.finalizando = true;
      try {
        await this.$store.dispatch("lavadero/cambiarEstado", {
          id: this.turnoAFinalizar.id,
          fuente: this.turnoAFinalizar.fuente,
          nuevo_estado: "finalizado",
        });
        this.aplicarMovimientoPendiente({ estado_lavadero: "finalizado" }, "finalizado");
        this.limpiarMovimientoPendiente();
        this.$store.dispatch("show_snackbar", {
          text: "Turno finalizado correctamente",
          color: "success",
        });
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al finalizar: " + (err.message || "Error desconocido"),
          color: "error",
        });
        this.revertirMovimientoPendiente();
        this.limpiarMovimientoPendiente();
      } finally {
        this.finalizando = false;
        this.dialogoConfirmarFinalizar = false;
        this.turnoAFinalizar = null;
      }
    },

    cancelarFinalizacion() {
      this.dialogoConfirmarFinalizar = false;
      this.turnoAFinalizar = null;
      this.revertirMovimientoPendiente();
      this.limpiarMovimientoPendiente();
    },

    async confirmarVueltaAPendiente() {
      if (!this.turnoAPendiente) return;

      this.volviendoAPendiente = true;
      try {
        const result = await this.$store.dispatch("lavadero/cambiarEstado", {
          id: this.turnoAPendiente.id,
          fuente: this.turnoAPendiente.fuente,
          nuevo_estado: "pendiente",
        });

        if (!result || result.exito !== 1) {
          this.revertirMovimientoPendiente();
          this.limpiarMovimientoPendiente();
          return;
        }

        this.aplicarMovimientoPendiente({
          estado_lavadero: "pendiente",
          lavador_id: null,
          lavador_nombre: "",
          equipo: 0,
        }, "pendiente");
        this.limpiarMovimientoPendiente();
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text:
            "Error al volver a pendiente: " + (err.message || "Error desconocido"),
          color: "error",
        });
        this.revertirMovimientoPendiente();
        this.limpiarMovimientoPendiente();
      } finally {
        this.volviendoAPendiente = false;
        this.dialogoConfirmarPendiente = false;
        this.turnoAPendiente = null;
      }
    },

    cancelarVueltaAPendiente() {
      this.dialogoConfirmarPendiente = false;
      this.turnoAPendiente = null;
      this.revertirMovimientoPendiente();
      this.limpiarMovimientoPendiente();
    },

    formatoFecha(fecha) {
      return moment(fecha).format("DD/MM/YYYY");
    },

    // --- Búsqueda SAI por patente ---
    onPatenteInput(val) {
      clearTimeout(this._buscarTimer);
      const patente = (val || "").trim().toUpperCase();

      // Resetear si la patente es muy corta
      if (patente.length < 6) {
        this.nuevoTurno.vin = "";
        this.nuevoTurno.modelo = "";
        this.modeloEncontrado = "";
        this.patenteEncontrada = "";
        this.patenteDuplicada = false;
        this.buscandoVehiculo = false;
        return;
      }

      // Verificar duplicado en turnos existentes
      const duplicada = this.turnos.some(
        (t) =>
          t.patente &&
          t.patente.toUpperCase().trim() === patente
      );
      this.patenteDuplicada = duplicada;

      // Debounce para buscar en SAI
      this._buscarTimer = setTimeout(() => {
        this.buscarVehiculoSAI(patente);
      }, 400);
    },

    async buscarVehiculoSAI(patente) {
      this.buscandoVehiculo = true;
      try {
        const result = await this.$store.dispatch(
          "lavadero/buscarVehiculo",
          patente
        );
        if (result.exito === 1 && result.data) {
          this.nuevoTurno.vin = result.data.VIN || "";
          this.nuevoTurno.modelo =
            result.data.ModeloDescripcion || result.data.Modelo || "";
          this.modeloEncontrado =
            result.data.ModeloDescripcion || result.data.Modelo || "";
          this.patenteEncontrada = result.data.Patente || "";
        } else {
          this.modeloEncontrado = "";
          this.patenteEncontrada = "";
        }
      } catch (err) {
        // Silencioso: si falla la búsqueda, el usuario completa manualmente
        console.warn("[LavaderoKanban] Error buscando vehículo:", err.message);
      } finally {
        this.buscandoVehiculo = false;
      }
    },

    // --- Crear turno manual ---
    allowedHours() {
      return [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
    },
    normalizarNombreArea(nombre) {
      return String(nombre || "").trim().toLowerCase();
    },
    autocompletarAreaRadaUsuario() {
      if (!this.dialogoCrearTurno || this.nuevoTurno.area_id != null) return;

      const areaUsuarioId = this.areaUsuarioId;
      if (areaUsuarioId == null) return;

      const areaUsuario = this.areas.find(
        (area) => String(area.Area_id) === String(areaUsuarioId)
      );
      if (
        areaUsuario &&
        this.normalizarNombreArea(areaUsuario.Nombre) === "rada"
      ) {
        this.nuevoTurno.area_id = areaUsuario.Area_id;
      }
    },
    abrirDialogoCrearTurno() {
      this.nuevoTurno = {
        patente: "",
        vin: "",
        modelo: "",
        fecha: moment().format("DD/MM/YYYY"),
        hora: "",
        sucursal: "1",
        area_id: null,
        comentario: "",
      };
      this.menuHora = false;
      this.patenteDuplicada = false;
      this.modeloEncontrado = "";
      this.patenteEncontrada = "";
      this.buscandoVehiculo = false;
      this.dialogoCrearTurno = true;
      this.autocompletarAreaRadaUsuario();
      // Cargar sucursales si aún no están en el store
      this.$store.dispatch("genericos/get_sucursales");
      // Cargar áreas si aún no están en el store
      if (this.areas.length === 0) {
        this.cargandoAreas = true;
        this.$store.dispatch("lavadero/fetchAreas").finally(() => {
          this.cargandoAreas = false;
          this.autocompletarAreaRadaUsuario();
        });
      }
    },
    cerrarDialogoCrearTurno() {
      this.dialogoCrearTurno = false;
      this.creandoTurno = false;
    },
    async crearTurno() {
      if (!this.formularioCrearValido) return;
      this.creandoTurno = true;
      try {
        // Convertir fecha de DD/MM/YYYY a YYYY-MM-DD para la API
        const fechaISO = moment(this.nuevoTurno.fecha, "DD/MM/YYYY").format(
          "YYYY-MM-DD"
        );
        await this.$store.dispatch("lavadero/crearTurno", {
          patente: (this.patenteEncontrada || this.nuevoTurno.patente).trim(),
          vin: this.nuevoTurno.vin.trim(),
          modelo: this.nuevoTurno.modelo.trim(),
          fecha: fechaISO,
          hora: this.nuevoTurno.hora,
          sucursal: "1",
          area_id: this.nuevoTurno.area_id || null,
          comentario: this.nuevoTurno.comentario.trim() || null,
        });
        this.$store.dispatch("show_snackbar", {
          text: "Turno creado correctamente",
          color: "success",
        });
        this.cerrarDialogoCrearTurno();
        this.fetchTurnos();
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text:
            "Error al crear el turno: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        this.creandoTurno = false;
      }
    },

    // --- Mobile back-button: cierra dialogs al presionar "atrás" ---

    /**
     * Cierra todos los dialogs abiertos y revierte cualquier movimiento pendiente.
     * Se llama desde el handler de popstate (botón atrás del celular).
     */
    _closeAllDialogs() {
      if (this.dialogoCrearTurno) this.cerrarDialogoCrearTurno();
      if (this.dialogoConfirmarFinalizar) this.cancelarFinalizacion();
      if (this.dialogoConfirmarPendiente) this.cancelarVueltaAPendiente();
      if (this.dialogoConfirmarAutoasignacion) this.cancelarAutoasignacion();
      if (this.dialogoAsignar) this.cancelarAsignacion();
      if (this.dialogoCambiarHorario) this.cancelarCambioHorario();
      if (this.dialogoModificarUnidad) this.cancelarModificarUnidad();
    },

    /**
     * Empuja una entrada fantasma en el historial del navegador
     * y registra un listener de popstate. Así el botón "atrás"
     * cierra el dialog en vez de navegar fuera de la página.
     */
    _pushDialogHistory() {
      if (this._popStateDialogHandler) return;
      this._popStateDialogHandler = () => {
        this._closeAllDialogs();
        // Volvemos a empujar estado para evitar la navegación real
        window.history.pushState(null, "", window.location.href);
      };
      window.history.pushState(null, "", window.location.href);
      window.addEventListener("popstate", this._popStateDialogHandler);
    },

    /** Limpia el listener de popstate cuando ya no hay dialogs abiertos. */
    _removeDialogHistory() {
      if (!this._popStateDialogHandler) return;
      window.removeEventListener("popstate", this._popStateDialogHandler);
      this._popStateDialogHandler = null;
    },

    /**
     * Sincroniza el estado del historial con la presencia de dialogs.
     * Solo activo en mobile; en desktop no interfiere.
     */
    _syncDialogHistory(shouldListen) {
      if (shouldListen && this.isMobileKanban) {
        this._pushDialogHistory();
      } else {
        this._removeDialogHistory();
      }
    },

  },
  mounted() {
    this.updateMobileKanbanLayout();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.updateMobileKanbanLayout);
    }
    this.fetchTurnos();
    this.$store.dispatch("lavadero/fetchPermisos");
    this.$store.dispatch("lavadero/fetchLavadores");
    this.$store.dispatch("lavadero/fetchAreas");
    this.$store.dispatch("genericos/get_sucursales");
    this._refreshInterval = setInterval(() => {
      this.fetchTurnos();
    }, 600000); // 10 minutos
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.updateMobileKanbanLayout);
    }
    this._removeDialogHistory();
    if (this._refreshInterval) {
      clearInterval(this._refreshInterval);
      this._refreshInterval = null;
    }
  },
};
</script>

<style>
.lavadero-fallback {
  opacity: 0.72;
  width: 100%;
  max-width: 280px;
}

.lavadero-fallback .tarjeta-lavadero {
  font-family: "Roboto", sans-serif;
  width: 100%;
  min-width: 0 !important;
  max-width: none !important;
  margin: 0 !important;
  overflow: hidden;
  border-radius: 16px !important;
  border: 1px solid var(--lavadero-card-border, rgba(15, 23, 42, 0.08)) !important;
  background: var(--lavadero-card-bg, #ffffff) !important;
  box-shadow: var(--lavadero-card-shadow, 0 10px 24px rgba(15, 23, 42, 0.08)) !important;
}

.lavadero-fallback .tarjeta-lavadero .v-card__text {
  padding: 12px 12px 30px !important;
}

.lavadero-fallback .tarjeta-lavadero .font-weight-bold.text-uppercase {
  display: block;
  min-width: 0;
  padding-right: 8px;
  color: var(--lavadero-card-title-color, #17202a) !important;
  font-size: 0.86rem !important;
  font-weight: 700 !important;
  line-height: 1.25;
  text-transform: uppercase;
}

.lavadero-fallback .tarjeta-lavadero .text-caption.grey--text {
  color: var(--lavadero-card-muted-color, inherit) !important;
}

.lavadero-fallback .tarjeta-lavadero .text-caption {
  font-size: 0.75rem !important;
  line-height: 1.35;
}

.lavadero-fallback .tarjeta-lavadero .v-chip {
  min-width: 58px;
  height: 22px !important;
  border-radius: 999px !important;
  font-size: 0.75rem !important;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.lavadero-fallback .tarjeta-lavadero .v-chip__content {
  width: 100%;
  justify-content: center;
}

.lavadero-fallback .tarjeta-lavadero .fecha-badge {
  right: 8px !important;
  bottom: 7px !important;
}

.lavadero-fallback .tarjeta-lavadero .menu-trigger {
  top: 6px !important;
  right: 6px !important;
  background: rgba(255, 255, 255, 0.9);
}
</style>

<style scoped>
.lavadero-kanban {
  min-height: 100%;
  padding: 8px 4px 88px;
  background:
    radial-gradient(circle at top left, rgba(25, 118, 210, 0.08), transparent 28rem),
    linear-gradient(180deg, rgba(0, 0, 0, 0.02), transparent 16rem);
}

.lavadero-toolbar {
  margin: 0;
  padding-top: 4px;
}

.lavadero-toolbar__field,
.lavadero-toolbar__actions {
  padding-left: 4px !important;
  padding-right: 4px !important;
}

.lavadero-toolbar__actions {
  display: flex;
}

.lavadero-toolbar__input ::v-deep .v-input__slot {
  border-radius: 14px !important;
  background-color: rgba(255, 255, 255, 0.92) !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.lavadero-toolbar__input ::v-deep .v-text-field--outlined.v-input--is-label-active .v-label,
.lavadero-toolbar__input ::v-deep .v-text-field--outlined.v-select--is-menu-active .v-label {
  background: #f5f7fb;
  padding: 0 4px;
  margin-left: -4px;
}

.lavadero-refresh-btn {
  width: 100%;
  border-radius: 12px;
  min-height: 38px;
  letter-spacing: 0.01em;
  box-shadow: 0 8px 18px rgba(25, 118, 210, 0.2) !important;
}

.lavadero-mobile-tabs {
  margin: 6px 4px 0;
  padding: 4px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.07);
}

.lavadero-mobile-tabs ::v-deep .v-tabs-bar {
  height: 42px;
  background: transparent !important;
}

.lavadero-mobile-tabs ::v-deep .v-slide-group__content {
  gap: 6px;
}

.lavadero-mobile-tabs__tab {
  min-width: 0 !important;
  height: 36px !important;
  padding: 0 8px !important;
  border-radius: 12px !important;
  color: #334155 !important;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.045em;
  text-transform: uppercase;
}

.lavadero-mobile-tabs__tab.v-tab--active {
  color: #1565c0 !important;
  background: rgba(25, 118, 210, 0.1);
}

.lavadero-mobile-tabs__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px !important;
  margin-left: 6px;
  padding: 0 6px !important;
  border-radius: 999px !important;
  color: #ffffff !important;
  background: #1976d2 !important;
  font-size: 0.68rem;
  font-weight: 800;
}

.lavadero-board-row {
  margin-left: 0;
  margin-right: 0;
}

.lavadero-board-col {
  padding-left: 0;
  padding-right: 0;
}

.lavadero-board ::v-deep .tablero-lavadero {
  height: auto;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  scrollbar-width: thin;
  padding-bottom: 8px;
}

.lavadero-board ::v-deep .tablero-lavadero::-webkit-scrollbar {
  height: 6px;
}

.lavadero-board ::v-deep .tablero-lavadero::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.22);
}

.lavadero-board ::v-deep .tablero-lavadero > .row {
  align-items: stretch;
  flex-wrap: nowrap;
  margin-left: -4px;
  margin-right: -4px;
}

.lavadero-board ::v-deep .columna-kanban {
  flex: 0 0 86vw;
  max-width: 86vw;
  padding: 6px 4px !important;
  scroll-snap-align: start;
}

.lavadero-board ::v-deep .columna-header {
  position: relative;
  overflow: hidden;
  min-height: 44px;
  margin-bottom: 8px !important;
  padding: 10px 12px !important;
  border-radius: 16px !important;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
}

.lavadero-board ::v-deep .columna-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: currentColor;
  opacity: 0.28;
}

.lavadero-board ::v-deep .columna-header .text-uppercase {
  position: relative;
  font-size: 0.78rem !important;
  letter-spacing: 0.075em !important;
}

.lavadero-board ::v-deep .columna-header .v-badge__badge {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 26px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-weight: 700;
  line-height: 1 !important;
  text-align: center;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.14);
}

.lavadero-board ::v-deep .columna-body {
  min-height: 140px;
  padding: 8px !important;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 18px !important;
  background: rgba(255, 255, 255, 0.68) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.lavadero-board ::v-deep .lavadero-draggable-item {
  margin-bottom: 10px;
}

.lavadero-board ::v-deep .lavadero-draggable-item:last-child {
  margin-bottom: 0;
}

.lavadero-board ::v-deep .tarjeta-lavadero {
  width: 100%;
  min-width: 0 !important;
  max-width: none !important;
  margin: 0 !important;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08) !important;
  border-radius: 16px !important;
  background: #ffffff !important;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08) !important;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.lavadero-board ::v-deep .tarjeta-lavadero:hover {
  transform: translateY(-1px);
  border-color: rgba(25, 118, 210, 0.22) !important;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.12) !important;
}

.lavadero-board ::v-deep .tarjeta-lavadero .v-card__text {
  padding: 12px 12px 30px !important;
}

.lavadero-board ::v-deep .tarjeta-lavadero .font-weight-bold.text-uppercase {
  display: block;
  min-width: 0;
  padding-right: 8px;
  color: #17202a;
  font-size: 0.86rem !important;
  line-height: 1.25;
}

.lavadero-board ::v-deep .tarjeta-lavadero .text-caption {
  line-height: 1.35;
}

.lavadero-board ::v-deep .tarjeta-lavadero .v-chip {
  min-width: 58px;
  height: 22px !important;
  border-radius: 999px !important;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.lavadero-board ::v-deep .tarjeta-lavadero .v-chip__content {
  width: 100%;
  justify-content: center;
}

.lavadero-board ::v-deep .fecha-badge {
  right: 8px !important;
  bottom: 7px !important;
}

.lavadero-board ::v-deep .menu-trigger {
  top: 6px !important;
  right: 6px !important;
  background: rgba(255, 255, 255, 0.9);
}

.lavadero-board ::v-deep .ghost {
  opacity: 0.72;
  border-radius: 16px;
  background: rgba(25, 118, 210, 0.12) !important;
}

.lavadero-board ::v-deep .chosen,
.lavadero-board ::v-deep .drag {
  transform: rotate(0.2deg);
}

.lavadero-fab {
  right: 18px !important;
  bottom: 18px !important;
  box-shadow: 0 12px 28px rgba(25, 118, 210, 0.32) !important;
}

.lavadero-dialog {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 32px);
  overflow: hidden;
  border-radius: 22px !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.26) !important;
}

.lavadero-dialog__title {
  display: flex;
  align-items: center;
  min-height: 60px;
  padding: 16px 18px !important;
  line-height: 1.2;
  letter-spacing: 0.01em;
}

.lavadero-dialog__title span {
  min-width: 0;
}

.lavadero-dialog__title-icon {
  flex: 0 0 auto;
  margin-right: 10px !important;
}

.lavadero-dialog__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 18px 16px 12px !important;
}

.lavadero-dialog__body ::v-deep .v-input__slot {
  border-radius: 12px !important;
}

.lavadero-dialog__actions {
  flex-wrap: wrap;
  padding: 12px 16px 16px !important;
  background: rgba(15, 23, 42, 0.02);
  gap: 2px;
  justify-content: flex-end;
}

.lavadero-dialog__action-row {
  display: flex;
  width: 100%;
  gap: 10px;
}

.lavadero-dialog__action-row--top {
  justify-content: flex-end;
}

.lavadero-dialog__action-row--bottom {
  justify-content: space-between;
}

.lavadero-dialog__btn {
  min-height: 40px;
  border-radius: 12px !important;
  letter-spacing: 0.01em;
  font-weight: 700;
  text-transform: none;
}

.lavadero-dialog__btn ::v-deep .v-btn__content {
  white-space: normal;
  line-height: 1.2;
}

.lavadero-dialog__btn--primary {
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.16) !important;
}

.lavadero-dialog__btn--primary:not(.v-btn--disabled) {
  color: #ffffff !important;
}

.lavadero-dialog__btn--primary.v-btn--disabled {
  opacity: 0.72;
  color: rgba(15, 23, 42, 0.52) !important;
  background-color: rgba(15, 23, 42, 0.08) !important;
  box-shadow: none !important;
}

.lavadero-dialog__btn--secondary.v-btn--text {
  border: 1px solid rgba(15, 23, 42, 0.16);
  color: #334155 !important;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06) !important;
}

.lavadero-dialog__btn--secondary.v-btn--text::before {
  background-color: currentColor;
  opacity: 0;
}

.lavadero-dialog__btn--secondary.v-btn--text:hover::before,
.lavadero-dialog__btn--secondary.v-btn--text:focus::before {
  opacity: 0.06;
}

.theme--dark .lavadero-kanban {
  background:
    radial-gradient(circle at top left, rgba(100, 181, 246, 0.12), transparent 28rem),
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), transparent 16rem);
}

.theme--dark .lavadero-toolbar__input ::v-deep .v-input__slot,
.theme--dark .lavadero-board ::v-deep .tarjeta-lavadero {
  background: #1e1e1e !important;
}

.theme--dark .lavadero-toolbar__input ::v-deep .v-text-field--outlined.v-input--is-label-active .v-label,
.theme--dark .lavadero-toolbar__input ::v-deep .v-text-field--outlined.v-select--is-menu-active .v-label {
  background: #1e1e1e;
}

.theme--dark .lavadero-mobile-tabs {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(30, 30, 30, 0.86);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.28);
}

.theme--dark .lavadero-mobile-tabs__tab {
  color: rgba(255, 255, 255, 0.76) !important;
}

.theme--dark .lavadero-mobile-tabs__tab.v-tab--active {
  color: #90caf9 !important;
  background: rgba(144, 202, 249, 0.12);
}

.theme--dark .lavadero-board ::v-deep .columna-body {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.theme--dark .lavadero-board ::v-deep .tarjeta-lavadero {
  border-color: rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28) !important;
}

.theme--dark .lavadero-board ::v-deep .tarjeta-lavadero .font-weight-bold.text-uppercase {
  color: rgba(255, 255, 255, 0.9);
}

.theme--dark .lavadero-board ::v-deep .menu-trigger {
  background: rgba(33, 33, 33, 0.92);
}

.theme--dark .lavadero-dialog {
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.52) !important;
}

.theme--dark .lavadero-dialog__actions {
  background: rgba(255, 255, 255, 0.04);
}

.theme--dark .lavadero-dialog__btn--secondary.v-btn--text {
  border-color: rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.86) !important;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: none !important;
}

.theme--dark .lavadero-dialog__btn--primary.v-btn--disabled {
  color: rgba(255, 255, 255, 0.48) !important;
  background-color: rgba(255, 255, 255, 0.12) !important;
}

@media (max-width: 599px) {
  .lavadero-dialog {
    max-height: calc(100vh - 24px);
    border-radius: 18px !important;
  }

  .lavadero-dialog__title {
    min-height: 56px;
    padding: 14px 16px !important;
    font-size: 1.12rem !important;
  }

  .lavadero-dialog__body {
    padding: 16px 14px 10px !important;
  }

  .lavadero-dialog__actions {
    flex-direction: column;
    align-items: stretch;
    padding: 12px 14px calc(14px + env(safe-area-inset-bottom)) !important;
  }

  .lavadero-dialog__spacer {
    display: none !important;
  }

  .lavadero-dialog__btn {
    width: 100%;
    min-height: 44px;
    margin: 0 !important;
  }

  .lavadero-dialog__action-row {
    flex-direction: column;
  }

  .lavadero-board--mobile-single ::v-deep .tablero-lavadero {
    overflow-x: hidden;
    padding-bottom: 0;
  }

  .lavadero-board--mobile-single ::v-deep .tablero-lavadero > .row {
    margin-left: 0;
    margin-right: 0;
  }

  .lavadero-board--mobile-single ::v-deep .columna-kanban {
    flex: 0 0 100%;
    max-width: 100%;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}

@media (min-width: 600px) {
  .lavadero-kanban {
    padding: 10px 8px 88px;
  }

  .lavadero-toolbar__search,
  .lavadero-toolbar__actions {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .lavadero-toolbar__actions {
    justify-content: flex-end;
  }

  .lavadero-refresh-btn {
    width: auto;
    min-width: 128px;
  }

  .lavadero-mobile-tabs {
    display: none;
  }

  .lavadero-dialog__btn {
    min-width: 118px;
  }

  .lavadero-board ::v-deep .columna-kanban {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
    padding: 8px !important;
  }

  .lavadero-board ::v-deep .columna-body {
    min-height: calc(100vh - 220px);
  }
}

@media (min-width: 960px) {
  .lavadero-kanban {
    padding: 12px 12px 96px;
  }

  .lavadero-board ::v-deep .columna-body {
    padding: 10px !important;
  }
}

@media (hover: none) {
  .lavadero-board ::v-deep .tarjeta-lavadero:hover {
    transform: none;
  }
}
</style>
